import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { getProduct, getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    getProduct(id)
      .then((p) => {
        setProduct(p);
        setSize(p.sizes[2] || p.sizes[0]);
        setColor(p.colors[0]);
      })
      .catch(() => setNotFound(true));
    getProducts()
      .then((all) => setRelated(all.filter((x) => x.id !== id).slice(0, 3)))
      .catch(() => {});
  }, [id]);

  if (notFound) {
    return (
      <div className="grain min-h-screen bg-black flex flex-col items-center justify-center px-6">
        <h1 className="font-display text-4xl font-bold text-white mb-6">Not on this line.</h1>
        <Link
          to="/shop"
          data-testid="notfound-back-btn"
          className="font-mono-tech text-xs uppercase tracking-widest text-steel-light hover:text-white transition-colors"
        >
          Back to catalogue
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="min-h-screen bg-black" />;
  }

  return (
    <div className="grain relative bg-black min-h-screen">
      <Nav />
      <main className="pt-28 pb-24 px-6 md:px-12">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-steel-light hover:text-white transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] overflow-hidden border border-white/10 bg-steel-dark/40"
          >
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover saturate-[0.55]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:pt-6"
          >
            <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-4">
              {product.category}
            </p>
            <h1
              data-testid="product-detail-name"
              className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter text-white leading-[0.9] mb-4"
            >
              {product.name}
            </h1>
            <p className="font-mono-tech text-xl text-white mb-8">${product.price.toFixed(2)}</p>
            <p className="text-steel-light leading-relaxed max-w-md mb-10">
              {product.description}
            </p>

            <div className="mb-8">
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-steel-light mb-3">
                Size
              </p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`detail-size-${s}`}
                    onClick={() => setSize(s)}
                    className={`font-mono-tech text-xs px-4 py-2 border transition-colors duration-200 ${
                      size === s
                        ? "bg-white text-black border-white"
                        : "text-white/80 border-white/25 hover:border-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-10">
              <p className="font-mono-tech text-[10px] uppercase tracking-widest text-steel-light mb-3">
                Colour
              </p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`detail-color-${c}`}
                    onClick={() => setColor(c)}
                    className={`font-mono-tech text-[11px] uppercase tracking-wide px-3 py-2 border transition-colors duration-200 ${
                      color === c
                        ? "bg-white text-black border-white"
                        : "text-white/80 border-white/25 hover:border-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <button
              data-testid="detail-add-to-cart"
              onClick={() => {
                addItem(product, size, color);
                toast.success(`${product.name} added`, { description: `${size} · ${color}` });
              }}
              className="group relative w-full md:w-auto overflow-hidden border border-white bg-white text-black px-12 py-4"
            >
              <span className="relative z-10 font-mono-tech text-xs uppercase tracking-widest flex items-center justify-center gap-2 group-hover:text-white transition-colors duration-300">
                <Check className="w-4 h-4" /> Add to Cart
              </span>
              <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            </button>
          </motion.div>
        </div>

        {related.length > 0 && (
          <div className="mt-28 border-t border-white/10 pt-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mb-10">
              Down the line
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  data-testid={`related-${p.id}`}
                  className="group block"
                >
                  <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover saturate-[0.5] group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                    />
                  </div>
                  <div className="flex items-baseline justify-between mt-3">
                    <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
                    <span className="font-mono-tech text-xs text-steel-light">
                      ${p.price.toFixed(0)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
